import type { MouseEvent } from 'react';
import { useMemo } from 'react';

import { apiGetShortUrl } from '#/api/shortUrl/apiShortUrl';
import type { ApiShortUrlType } from '#/api/shortUrl/types/ApiShortUrlType';
import { Box } from '#/components/Box/Box';
import { Icon } from '#/components/Icon/Icon';
import type { IconName } from '#/components/Icon/IconName';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { StyledShareLink } from './ShareStyleButton';
import { getSocialLink } from './helpers/getSocialLink';
import { makeParameters } from './helpers/makeParameters';
import { withSocialUtm } from './helpers/withSocialUtm';

const socialWindowFeatures =
  'scrollbars=yes,menubar=no,width=700,height=600,resizable=yes,toolbar=no,location=no,status=no';

type SocialType = 'facebook' | 'x' | 'email' | 'linkedin';

const IconNameBySocialType: Record<'facebook' | 'x' | 'linkedin', IconName> = {
  facebook: 'social/facebook',
  x: 'social/x',
  linkedin: 'social/linkedin',
};

type Props = {
  eventName: string;
  eventProperties?: Record<string, unknown>;
  object: {
    id: string;
    url: string | Record<string, string>;
  };
  objectType?: ApiShortUrlType;
  socialType: SocialType;
  utmParams: Record<string, string>;
  message?: string;
  subject?: string;
  onClick?: () => void;
  width?: number;
  height?: number;
};

const LABELS = (i18n: I18nFn) => ({
  facebook: i18n('Share on Facebook'),
  x: i18n('Share on X'),
  email: i18n('Share by e-mail'),
  linkedin: i18n('Share on LinkedIn'),
});

export function ShareButton({
  eventName,
  eventProperties,
  object,
  objectType,
  socialType,
  utmParams,
  message,
  subject,
  onClick,
  ...props
}: Props) {
  const { i18n, locale } = useI18n();

  const labels = LABELS(i18n);

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    trackClicked(eventName, eventProperties ?? {});
    let linkPs: Promise<string>;
    let popup: Window;

    const isIosMobileApp = window.navigator.userAgent.includes(
      'Idealist Mobile App iOS',
    );

    if (!isIosMobileApp) {
      // We have to create the window before fetching the shorturl to prevent
      // iOS web Safari from blocking it.
      // @ts-expect-error TS(2322): Type 'Window | null' is not assignable to type 'Wi... Remove this comment to see the full error message
      popup = window.open('', `${socialType}-share`, socialWindowFeatures);
    }

    const localizedUrl =
      typeof object.url === 'string' ? object.url : object.url[locale];

    if (objectType) {
      linkPs = apiGetShortUrl({
        toType: objectType,
        toId: object.id,
        toUrl: localizedUrl ?? null,
        extraParams: makeParameters(withSocialUtm(utmParams, socialType)),
        locale,
      });
    } else {
      // Only use link shortener for Idealist links
      linkPs = localizedUrl?.startsWith('/')
        ? apiGetShortUrl({
            toType: 'URL',
            toUrl: localizedUrl,
            extraParams: makeParameters(withSocialUtm(utmParams, socialType)),
            locale,
          }) // No shortening for links that don't start with '/'
        : Promise.resolve(localizedUrl ?? '');
    }

    linkPs.then((shortResourceLink) => {
      const link = getSocialLink(
        i18n,
        socialType,
        shortResourceLink,
        message,
        objectType,
        subject,
      );

      if (isIosMobileApp) {
        window.location.href = link;
      } else {
        popup.location.href = link;
        setTimeout(() => {
          if (socialType === 'email' && popup.location.href === 'about:blank') {
            popup.close();
          }
        }, 1000);
      }
    });

    if (onClick) {
      onClick();
    }
  };

  const href = useMemo(() => {
    if (!object.url) return '';

    const resolvedUrl =
      typeof object.url === 'string' ? object.url : (object.url[locale] ?? '');
    return getSocialLink(
      i18n,
      socialType,
      objectType
        ? `https://idealist.org${resolvedUrl.startsWith('/') ? '' : '/'}${resolvedUrl}`
        : resolvedUrl,
      message,
      objectType,
      subject,
    );
  }, [i18n, locale, message, object.url, objectType, socialType, subject]);

  return (
    <Box
      onClick={handleClick}
      display="flex"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <StyledShareLink
        data-qa-id={`social-button-${socialType}`}
        href={href}
        target="_blank"
        tabIndex={0}
        aria-label={labels[socialType]}
        title={labels[socialType]}
      >
        {socialType === 'email' ? (
          <Icon name="mail" colorName="white" size={25} />
        ) : (
          <Icon
            name={IconNameBySocialType[socialType]}
            colorName="white"
            size={25}
          />
        )}
      </StyledShareLink>
    </Box>
  );
}
